import { createStackNavigator } from '@react-navigation/stack';
import { Module } from '../Routes';
import { DispatchSchemas } from '../schemas/dispatch';
import { DispatchParamList } from '../typings/Params';

import PatrolIndicators from '../pages/dispatch/PatrolIndicators/PatrolIndicators';
import moment from 'moment';

import {
    faClipboardList,
    faBriefcase,
    faBuildingFlag,
    faBuildingShield,
    faCar,
    faCube,
    faFlagCheckered,
    faFolderOpen,
    faHandsBound,
    faHomeLg,
    faListAlt,
    faPause,
    faPeopleGroup,
    faShield,
    faTag,
    faUser,
    faUserGear,
    faUsers,
    faPersonWalkingArrowRight,
    faHandcuffs,
    faForwardFast,
    faPersonWalking,
} from '@fortawesome/free-solid-svg-icons';
import {
    faArrowUpSquareTriangle,
} from '@fortawesome/pro-solid-svg-icons';


// dispatch
import Assignments from '../pages/dispatch/Assignments/Assignment';
import CityProperties from '../pages/dispatch/CityProperties/CityProperties';
import CityPropertyForm from '../pages/dispatch/CityProperties/CityPropertyForm';
import DispatchOccurrenceDetail from '../pages/dispatch/DispatchOccurrenceDetail';
import Equipment from '../pages/dispatch/Equipments/Equipment';
import FinishCategories from '../pages/dispatch/FinishCategories/index';
import ObjectsCategories from '../pages/dispatch/ObjectCategories/ObjectCategory';
import Occurrences from '../pages/dispatch/Occurrences/Occurrences';
import OccurrenceTags from '../pages/dispatch/OccurrenceTags/OccurrenceTags';
import OccurrenceTypes from '../pages/dispatch/OccurrenceTypes/OccurrenceTypes';
import PersonRelations from '../pages/dispatch/PersonRelation/PersonRelations';
import PoliceStationForm from '../pages/dispatch/PoliceStations/PoliceStationForm';
import PoliceStations from '../pages/dispatch/PoliceStations/PoliceStations';
import Providences from '../pages/dispatch/Providences/Providences';
import DispatchReports from '../pages/dispatch/Reports/Reports';
import DispatchReportTags from '../pages/dispatch/ReportTags/ReportTags';
import Resume from '../pages/dispatch/Resume/Resume';
import TeamNames from '../pages/dispatch/TeamNames/TeamNames';
import TeamName from '../pages/dispatch/TeamNames/TeamName';
import TeamPrograms from '../pages/dispatch/TeamPrograms/TeamPrograms';
import UnitPauseReasons from '../pages/dispatch/UnitPauseReasons/UnitPauseReasons';
import UnitForm from '../pages/dispatch/Units/UnitForm';
import Units from '../pages/dispatch/Units/Units';
import UnitDisplacedReasons from '../pages/dispatch/UnitDisplacedReasons/UnitDisplacedReasons';
import VehicleRelations from '../pages/dispatch/VehicleRelation/VehicleRelations';
import HandcuffReasons from '../pages/dispatch/HandcuffReasons/HandcuffReasons';
import UnitFinishEarlyReasons from '../pages/dispatch/UnitFinishEarlyReasons/UnitFinishEarlyReasons';
import PatrolActivities from '../pages/dispatch/PatrolActivities/PatrolActivities';
import EventOperations from '../pages/dispatch/EventOperations/EventOperations';
import { faCalendarUsers, faCars } from '@fortawesome/pro-regular-svg-icons';
import UnitCategories from '../pages/dispatch/UnitCategories/UnitCategories';
import UnitCategory from '../pages/dispatch/UnitCategories/UnitCategory';


export const dispatchModule: Module<'Dispatch'> = {
    path: '/dispatch',
    label: 'dispatch',
    stackNavigator: createStackNavigator<DispatchParamList>(),
    canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
    icon: require('../../assets/icon_dispatch.png'),
    logo: require('../../assets/logo_dispatch.png'),
    navigate: (navigation) => navigation.navigate('Dispatch'),
    schemas: DispatchSchemas,
    screens: {
        Occurrences: {
            path: 'occurrences',
            selectedItem: 'occurrences',
            label: 'occurrences',
            initialParams: () => ({
                page: 1,
                beginIn: moment().subtract(1, 'week').toDate().getTime(),
                limit: 25,
            }),
            accessInformation: {
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'Occurrences' }),
                icon: faClipboardList,
            },
            component: Occurrences,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_occurrences
                    || user?.permissions.dispatch_manage_all_acting_bodies_occurrences
                    || user?.permissions.reopen_occurrence
                    || user?.permissions.transfer_occurrence
                    || user?.permissions.dispatch_manage_occurrences_bulk_finish
                    || user?.permissions.dispatch_manage_all_acting_bodies_occurrences_bulk_finish),
        },
        DispatchOccurrenceDetail: {
            path: 'occurrences/detail/:occurrenceId?',
            selectedItem: 'occurrences',
            component: DispatchOccurrenceDetail,
            label: 'occurrence',
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_occurrences
                    || user?.permissions.dispatch_manage_all_acting_bodies_occurrences
                    || user?.permissions.reopen_occurrence
                    || user?.permissions.transfer_occurrence
                    || user?.permissions.dispatch_manage_occurrences_bulk_finish
                    || user?.permissions.dispatch_manage_all_acting_bodies_occurrences_bulk_finish),
        },
        OccurrenceTags: {
            path: 'occurrence-tags',
            selectedItem: 'occurrence-tags',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            label: 'occurrenceTags',
            accessInformation: {
                icon: faTag,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'OccurrenceTags' }),
            },
            component: OccurrenceTags,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_occurrence_tags
                    || user?.permissions.dispatch_manage_all_acting_bodies_occurrence_tags),
        },
        OccurrenceTypes: {
            path: 'occurrence-types',
            selectedItem: 'occurrence-types',
            initialParams: () => ({
                page: 1,
                isActive: true,
                limit: 25,
            }),
            label: 'occurrence_types',
            accessInformation: {
                icon: faListAlt,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'OccurrenceTypes' }),
            },
            component: OccurrenceTypes,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_all_acting_bodies_occurrence_types),
        },
        Equipments: {
            path: 'equipments',
            selectedItem: 'equipments',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            label: 'equipments',
            accessInformation: {
                icon: faBriefcase,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'Equipments' }),
            },
            component: Equipment,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_equipments
                    || user?.permissions.dispatch_manage_all_acting_bodies_equipments),
        },
        DispatchReports: {
            path: 'reports',
            selectedItem: 'reports',
            label: 'reports',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faFolderOpen,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'DispatchReports' }),
            },
            component: DispatchReports,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_download_report
                    || user?.permissions.dispatch_manage_all_acting_bodies_download_report
                    || user?.permissions.dispatch_generate_report
                    || user?.permissions.dispatch_manage_all_acting_bodies_reports),
        },
        ReportTags: {
            path: 'report-tags',
            selectedItem: 'report-tags',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            label: 'reportTags',
            accessInformation: {
                icon: faTag,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'ReportTags' }),
            },
            component: DispatchReportTags,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_report_tags
                    || user?.permissions.dispatch_manage_all_acting_bodies_report_tags),
        },
        Assignments: {
            path: 'assignments',
            selectedItem: 'assignments',
            label: 'assignments',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faUserGear,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'Assignments' }),
            },
            component: Assignments,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_assignments
                    || user?.permissions.dispatch_manage_all_acting_bodies_assignments),
        },
        VehicleRelations: {
            path: 'vehicle-relations',
            selectedItem: 'vehicle-relations',
            label: 'vehicleRelations',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faCar,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'VehicleRelations' }),
            },
            component: VehicleRelations,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_vehicle_relations
                    || user?.permissions.dispatch_manage_all_acting_bodies_vehicle_relations),
        },
        PersonRelations: {
            path: 'person-relations',
            selectedItem: 'person-relations',
            label: 'personRelations',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faUser,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'PersonRelations' }),
            },
            component: PersonRelations,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_person_relations
                    || user?.permissions.dispatch_manage_all_acting_bodies_person_relations),
        },
        ObjectsCategories: {
            path: 'objects-categories',
            selectedItem: 'objects-categories',
            label: 'objectsCategories',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faCube,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'ObjectsCategories' }),
            },
            component: ObjectsCategories,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_objects_categories
                    || user?.permissions.dispatch_manage_all_acting_bodies_objects_categories),
        },
        TeamNames: {
            path: 'team-names',
            selectedItem: 'team-names',
            label: 'teamNames',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faUsers,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'TeamNames' }),
            },
            component: TeamNames,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_team_names
                    || user?.permissions.dispatch_manage_all_acting_bodies_unit_names
                    || user?.permissions.dispatch_manage_acting_body_units_unit_names),
        },
        TeamName: {
            path: 'team-names/form/:id?',
            selectedItem: 'team-names',
            label: 'teamNames',
            component: TeamName,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_team_names
                    || user?.permissions.dispatch_manage_all_acting_bodies_unit_names
                    || user?.permissions.dispatch_manage_acting_body_units_unit_names),
        },
        UnitCategories: {
            path: 'unit-category',
            selectedItem: 'unit-category',
            label: 'unitCategory',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faCars,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'UnitCategories' }),
            },
            component: UnitCategories,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_unit_category
                    || user?.permissions.dispatch_manage_all_acting_bodies_unit_category),
        },
        UnitCategory: {
            path: 'unit-category/form/:id?',
            selectedItem: 'unit-category',
            label: 'unitCategory',
            component: UnitCategory,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_unit_category
                    || user?.permissions.dispatch_manage_all_acting_bodies_unit_category),
        },
        TeamPrograms: {
            path: 'team-programs',
            selectedItem: 'team-programs',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            label: 'teamPrograms',
            accessInformation: {
                icon: faPeopleGroup,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'TeamPrograms' }),
            },
            component: TeamPrograms,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_unit_programs
                    || user?.permissions.dispatch_manage_all_acting_bodies_unit_programs),
        },
        Units: {
            path: 'units',
            selectedItem: 'units',
            label: 'teams',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faShield,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'Units' }),
            },
            component: Units,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_units
                    || user?.permissions.dispatch_manage_acting_body_units
                    || user?.permissions.dispatch_manage_all_acting_bodies_units
                    || user?.permissions.dispatch_pause_units),
        },
        UnitForm: {
            path: 'units/form/:id?',
            selectedItem: 'units',
            label: 'team',
            component: UnitForm,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_units
                    || user?.permissions.dispatch_manage_acting_body_units
                    || user?.permissions.dispatch_manage_all_acting_bodies_units
                    || user?.permissions.dispatch_pause_units),
        },
        FinishCategories: {
            path: 'finish-categories',
            selectedItem: 'finish-categories',
            label: 'finishCategories',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faFlagCheckered,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'FinishCategories' }),
            },
            component: FinishCategories,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_finish_categories
                    || user?.permissions.dispatch_manage_all_acting_bodies_finish_categories),
        },
        Resume: {
            path: 'resume',
            selectedItem: 'resume',
            label: 'general_vision',
            accessInformation: {
                icon: faHomeLg,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'Resume' }),
            },
            component: Resume,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_see_resume),
        },
        PoliceStations: {
            path: 'police-stations',
            selectedItem: 'police-stations',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            label: 'policeStations',
            accessInformation: {
                icon: faBuildingShield,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'PoliceStations' }),
            },
            component: PoliceStations,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_police_stations),
        },
        PoliceStationForm: {
            path: 'police-stations/form/:id?',
            selectedItem: 'police-stations',
            label: 'policeStations',
            component: PoliceStationForm,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_police_stations),
        },
        Providences: {
            path: 'providences',
            selectedItem: 'providences',
            label: 'providences',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faHandsBound,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'Providences' }),
            },
            component: Providences,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_providences
                    || user?.permissions.dispatch_manage_all_acting_bodies_providences),
        },
        UnitPauseReasons: {
            path: 'unit-pause-reasons',
            selectedItem: 'unit-pause-reasons',
            label: 'unitPauseReasons',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faPause,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'UnitPauseReasons' }),
            },
            component: UnitPauseReasons,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_unit_pause_reasons
                    || user?.permissions.dispatch_manage_acting_body_units_unit_pause_reasons
                    || user?.permissions.dispatch_manage_all_acting_bodies_unit_pause_reasons),
        },
        UnitFinishEarlyReasons: {
            path: 'unit-finish-early-reasons',
            selectedItem: 'unit-finish-early-reasons',
            label: 'unitFinishEarlyReasons',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faForwardFast,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'UnitFinishEarlyReasons' }),
            },
            component: UnitFinishEarlyReasons,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_manage_unit_finish_early_reasons
                    || user?.permissions.dispatch_manage_acting_body_units_unit_finish_early_reasons
                    || user?.permissions.dispatch_manage_all_acting_bodies_unit_finish_early_reasons),
        },
        UnitDisplacedReasons: {
            path: 'unit-displaced-reasons',
            selectedItem: 'unit-displaced-reasons',
            label: 'unitDisplacedReasons',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faPersonWalkingArrowRight,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'UnitDisplacedReasons' }),
            },
            component: UnitDisplacedReasons,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_unit_displaced_reasons
                    || user?.permissions.dispatch_manage_all_acting_bodies_unit_displaced_reasons),
        },
        CityProperties: {
            path: 'city-properties',
            selectedItem: 'city-properties',
            label: 'cityProperties',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faBuildingFlag,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'CityProperties' }),
            },
            component: CityProperties,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_city_property
                    || user?.permissions.dispatch_manage_all_acting_bodies_city_property),
        },
        CityPropertyForm: {
            path: 'city-properties/form/:id?',
            selectedItem: 'city-properties',
            label: 'cityProperties',
            component: CityPropertyForm,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_city_property
                    || user?.permissions.dispatch_manage_all_acting_bodies_city_property),
        },
        HandcuffReasons: {
            path: 'handcuff-reasons',
            selectedItem: 'handcuff-reasons',
            label: 'handcuffReasons',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faHandcuffs,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'HandcuffReasons' }),
            },
            component: HandcuffReasons,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_handcuff_reasons
                    || user?.permissions.dispatch_manage_all_acting_bodies_handcuff_reasons),
        },
        PatrolIndicators: {
            path: 'patrol-indicators',
            selectedItem: 'patrol-indicators',
            label: 'patrolIndicators',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faArrowUpSquareTriangle,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'PatrolIndicators' }),
            },
            component: PatrolIndicators,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_manage_patrol_indicators
                    || user?.permissions.dispatch_manage_all_acting_bodies_patrol_indicators),
        },
        PatrolActivities: {
            path: 'patrol-activities',
            selectedItem: 'patrol-activities',
            label: 'patrolActivities',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faPersonWalking,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'PatrolActivities' }),
            },
            component: PatrolActivities,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_manage_patrol_activities
                    || user?.permissions.dispatch_manage_all_acting_bodies_patrol_activities),
        },
        EventOperations: {
            path: 'event-operations',
            selectedItem: 'event-operations',
            label: 'eventOperations',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            accessInformation: {
                icon: faCalendarUsers,
                navigate: (nav) => nav.navigate('Dispatch', { screen: 'EventOperations' }),
            },
            component: EventOperations,
            canAccess: (user) =>
                Boolean(user?.isAdmin
                    || user?.permissions.dispatch_module_access
                    || user?.permissions.dispatch_manage_event_operations
                    || user?.permissions.dispatch_manage_all_acting_bodies_event_operations),
        },
    },
};
